import { FC, useMemo, useCallback, useEffect } from 'react'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { useRequestErrors } from 'hooks/useErrors'
import { createPaymentMethodRequestSelector, deletePaymentMethodRequestSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { ResetCreateCCPaymentMethodAction } from 'components/features/creditCard/creditCardStore/actions'
import { CreditCardPanePreset } from 'components/features/creditCard/creditCardPanes/CreditCardPanePreset'
import FloatingPaneNotification from 'components/features/pfm/FloatingPaneNotification/FloatingPaneNotification'
import AddPaymentMethodForm, { usePaymentMethodForm } from 'components/features/creditCard/creditCardPanes/paneForms/AddPaymentMethodForm'
import BankSvg from 'icons/linkedaccounts.svg'
import SvgIcon from 'components/atoms/SvgIcon'

const AddPaymentMethodPane: FC<{}> = () => {
  const formContext = usePaymentMethodForm()
  const { watch, formState: { isValid, touchedFields, isSubmitting, isSubmitSuccessful }} = formContext
  const fields = watch()
  const fieldsCount = Object.keys(fields).length
  const touchedFieldsCount = Object.keys(touchedFields).length

  const createMethodrequest = useStoreSelector(createPaymentMethodRequestSelector)
  const deleteMethodRequest = useStoreSelector(deletePaymentMethodRequestSelector)
  const requestErrors = useRequestErrors({ request: createMethodrequest })

  const dispatch = useStoreDispatch()
  const handleDismissErrors = useCallback(() => {
    dispatch(new ResetCreateCCPaymentMethodAction())
    formContext.clearErrors()
  }, [dispatch, formContext])

  const errorNotificationChild = useMemo(() => !!requestErrors && requestErrors.length && (
    <FloatingPaneNotification
      messages={ requestErrors }
      onDismiss={ handleDismissErrors }
    />
  ), [requestErrors, handleDismissErrors])

  useEffect(() => {
    handleDismissErrors()
  }, [handleDismissErrors])

  const progressPercent = useMemo(() => {
    const STARTING_AMOUNT = 5
    if (isSubmitting || isSubmitSuccessful) {
      return 100
    } else if (isValid) {
      return 80
    }
    return touchedFieldsCount * (70 / fieldsCount) || STARTING_AMOUNT
  }, [isSubmitting, isSubmitSuccessful, isValid, touchedFieldsCount, fieldsCount])

  return (
    <CreditCardPanePreset
      title='Add Payment Method'
      notification={ errorNotificationChild }
      isLoading={ createMethodrequest.isLoading || deleteMethodRequest.isLoading }
      progressBarPercent={ progressPercent }
    >
      <SvgIcon src={ BankSvg } size='large' />
      <section>
        <h2>Enter your bank account information</h2>
        <p>
          Save a bank account you want to use to make payments on your
          credit card.
        </p>
      </section>
      <AddPaymentMethodForm formContext={ formContext } />
      <small>
        By clicking 'Save Account', you are providing your electronic signature
        and authorizing us to debit your account.
      </small>
    </CreditCardPanePreset>
  )
}

export default AddPaymentMethodPane
